import {
  Column,
  Container,
  Copy,
  Flex,
  Heading,
  Image,
  Row,
  Section,
  Square,
  Stack,
  SvgCaretLeft,
  SvgInfo,
} from '@grubhubprod/gh-cookbook-react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PdfButton } from 'src/components/selfService/features/OrderHistory/PdfButton/PdfButton';
import PrintContentContainer from 'src/components/Print/PrintContentContainer';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import {
  FeeType,
  IDeliveryInfo,
  ILineItem,
  IOrderChargesFeeItems,
  IOrderPayment,
  IOrdersReceipt,
  IParticipantLineMap,
  OrderPaymentType,
} from 'src/types/OrderHistory';
import { sdk } from 'src/utilities/sdk';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { getFormattedTimezoneFromObject } from 'src/utilities/time-utils';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { getConfig } from 'src/utilities/config';
import {
  createItems,
  createParticipantLines,
  orderPaymentTypeIsReward,
} from 'src/common/Order/utils';
import { GroupOrderLines } from 'src/common/Order/OrderReceipt/GroupOrderLines';
import { RouterLink } from 'src/components/RouterLink/RouterLink';
import { ReceiptLineItem } from 'src/components/selfService/features/OrderHistory/ReceiptLineItem/ReceiptLineItem';
import { FeeLineItem } from 'src/components/selfService/features/OrderHistory/FeeLineItem/FeeLineItem';
import { DeliveryFeeSection } from 'src/components/selfService/features/OrderHistory/DeliveryFeeSection/DeliveryFeeSection';
import {
  calculateServiceFee,
  getGenericFees,
  isGrubhubPlusPayment,
} from 'src/components/selfService/features/OrderHistory/OrderReceipt.utils';
import { formatPenniesToCurrency } from 'src/common/utils/formatPenniesToCurrency';
import { MEAL_CREDIT_ENTITY_NAME } from 'src/constants/mealCredit';
import { capitalize } from 'src/utilities/string-utils';

const DATE_FORMAT = 'MMM d, yyyy h:mma';

export default function OrderReceipt(): JSX.Element {
  const { isTablet, isDesktop, isPhone } = useScreenSizes();
  const { corpid, orderId } = useParams<{ corpid: string; orderId: string }>();

  const { grubhubLogo } = getConfig();
  const [order, setOrder] = useState<IOrdersReceipt>(null);
  const [isGroupOrder, setIsGroupOrder] = useState<boolean>(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState<boolean>(false);
  const [participantLines, setParticipantLines] =
    useState<IParticipantLineMap[]>(null);
  const [grubhubPlusPayment, setGrubhubPlusPayment] = useState<IOrderPayment>();
  const [promoCodeAmount, setPromoCodeAmount] = useState(0);
  const [rewardsAmount, setRewardsAmount] = useState(0);
  const [dinerPaymentAmount, setDinerPaymentAmount] = useState(0);

  const { sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    // send analytics
    sendTealiumPageView({
      pageGroup: 'corporate self service - account management',
      pageName: 'Order Receipt',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function setOrderPaymentDetails(orderPayments: IOrderPayment[]): void {
      if (!orderPayments) {
        return;
      }

      let rewardsAmount = 0;
      let dinerAmount = 0;

      orderPayments.forEach((payment) => {
        const isSubscriptionPayment = payment.metadata?.subscription_code;
        const isSelfDeliveryMerchantBenefit =
          payment.metadata?.self_delivery_merchant_benefit === 'true';
        if (isGrubhubPlusPayment(payment)) {
          setGrubhubPlusPayment(payment);
        } else if (
          payment.type === OrderPaymentType.PROMO_CODE &&
          !isSubscriptionPayment &&
          !isSelfDeliveryMerchantBenefit
        ) {
          const promoCodeAmount = payment.amount || 0;
          setPromoCodeAmount(promoCodeAmount);
        } else if (orderPaymentTypeIsReward(payment.type)) {
          rewardsAmount += payment.amount || 0;
        } else if (
          payment.type !== OrderPaymentType.CORPORATE_LINE_OF_CREDIT &&
          payment.type !== OrderPaymentType.PROMO_CODE
        ) {
          // other diner's payments
          dinerAmount += payment.amount || 0;
        }
      });

      setRewardsAmount(rewardsAmount);
      setDinerPaymentAmount(dinerAmount);
    }

    async function getOrder() {
      try {
        const response: IOrdersReceipt = await sdk.http.get(
          `/corps/${corpid}/orders/${orderId}`
        );

        setOrderPaymentDetails(get(response, 'order_payments', 'payments'));
        setIsGroupOrder(get(response, 'group_order'));
        setIsGroupAdmin(get(response, 'group_admin'));
        if (response.group_order) {
          setParticipantLines(
            createParticipantLines(
              response.group_order,
              get(response, 'group_order_info')
            )
          );
        }
        setOrder(response);
      } catch (error) {
        pushErrorFromAPI(error);
      }
    }
    getOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  return order ? (
    <Container variant="default">
      <PrintContentContainer id="receipt-content">
        <Section inset="md">
          <Stack y={isPhone ? 'md' : 'lg'} className="print_hidden">
            <Flex
              justifyContent="space-between"
              flexDirection={isPhone ? 'column' : 'row'}
            >
              <Stack y={isPhone ? 'x-sm' : 'none'}>
                <Square>
                  <Copy variant="body" color="interactive">
                    <SvgCaretLeft size="16px" />
                  </Copy>

                  <RouterLink
                    href={`/corporate/management/${corpid}/orderHistory`}
                  >
                    Back to order history
                  </RouterLink>
                </Square>
              </Stack>
            </Flex>
          </Stack>

          <Stack
            y="md"
            className="print_visibleForPrint"
            borderBottomWidth="1px"
            borderBottomStyle="solid"
            borderBottomColor="lightLine"
          >
            <Stack y="xxx-sm">
              <Image width="84px" src={grubhubLogo} alt="Grubhub logo" />
            </Stack>
          </Stack>

          <Row gutter={0}>
            <Column
              desktop={4}
              tablet={12}
              gutter={0}
              className="orderReceipt_dinerInfoSection"
            >
              <Stack x="xx-lg">
                {isDesktop && (
                  <Square>
                    <Stack y="md">
                      <Stack y="x-sm">
                        <Heading variant="h5">Ordered by</Heading>
                      </Stack>
                      <Flex flexDirection="column">
                        <Copy variant="body">{order.ordered_by.name}</Copy>
                        <Copy variant="body">{order.ordered_by.email}</Copy>
                      </Flex>
                    </Stack>
                    <Stack y="md">
                      <Stack y="x-sm">
                        <Heading variant="h5">Delivery date</Heading>
                      </Stack>
                      <Copy variant="body">
                        {getFormattedTimezoneFromObject(
                          order.delivered_on_utc,
                          order.time_zone,
                          DATE_FORMAT
                        )}
                      </Copy>
                    </Stack>
                    <Stack y="md">
                      <Stack y="x-sm">
                        <Heading variant="h5">Delivery address</Heading>
                      </Stack>
                      <Copy variant="body">
                        {formatAddress(order.delivery_info)}
                      </Copy>
                    </Stack>
                    <Heading variant="h5">Order number</Heading>
                    <Copy variant="body">{order.order_number}</Copy>
                  </Square>
                )}
                {(isPhone || isTablet) && (
                  <Stack y="lg">
                    <Square>
                      <Stack y={isTablet ? 'md' : 'none'}>
                        <Row gutter={0}>
                          <Column tablet={6} phone={12} gutter={0}>
                            <Stack y={isPhone ? 'md' : 'none'}>
                              <Stack y="x-sm">
                                <Heading variant="h5">Ordered by</Heading>
                              </Stack>
                              <Flex flexDirection="column">
                                <Copy variant="body">
                                  {order.ordered_by.name}
                                </Copy>
                                <Copy variant="body">
                                  {order.ordered_by.email}
                                </Copy>
                              </Flex>
                            </Stack>
                          </Column>
                          <Column tablet={6} phone={6} gutter={0}>
                            <Stack y={isPhone ? 'md' : 'none'}>
                              <Stack y="x-sm">
                                <Heading variant="h5">Delivery date</Heading>
                              </Stack>
                              <Copy variant="body">
                                {getFormattedTimezoneFromObject(
                                  order.delivered_on_utc,
                                  order.time_zone,
                                  DATE_FORMAT
                                )}
                              </Copy>
                            </Stack>
                          </Column>
                        </Row>
                      </Stack>
                      <Stack y="md">
                        <Row gutter={0}>
                          <Column tablet={6} phone={12} gutter={0}>
                            <Stack y={isPhone ? 'md' : 'none'}>
                              <Stack y="x-sm">
                                <Heading variant="h5">Delivery address</Heading>
                              </Stack>
                              <Copy variant="body">
                                {formatAddress(order.delivery_info)}
                              </Copy>
                            </Stack>
                          </Column>
                          <Column tablet={6} phone={6} gutter={0}>
                            <Stack y="x-sm">
                              <Heading variant="h5">Order number</Heading>
                            </Stack>
                            <Copy variant="body">{order.order_number}</Copy>
                          </Column>
                        </Row>
                      </Stack>
                    </Square>
                  </Stack>
                )}
              </Stack>
            </Column>

            <Column
              desktop={8}
              tablet={12}
              phone={12}
              gutter={0}
              className="orderReceipt_lineItemsSection"
            >
              <Stack y="md">
                <Square
                  dimension="2"
                  bg="untintedBackground"
                  className="print_cancelBorders"
                >
                  <Square
                    borderBottomWidth="1px"
                    borderBottomStyle="solid"
                    borderBottomColor="lightLine"
                  >
                    <Section inset="md" className="print_cancelPaddingTop">
                      <Stack y="x-sm">
                        <Copy variant="lead" color="secondaryText">
                          {isGroupOrder ? 'Group order from' : 'Restaurant'}
                        </Copy>
                      </Stack>
                      <Flex justifyContent="space-between">
                        <Heading variant="h2">
                          {order.ordered_from.restaurant_name}
                        </Heading>
                        <PdfButton
                          corpId={corpid as string}
                          orderId={orderId as string}
                          order={order}
                        />
                      </Flex>
                    </Section>
                  </Square>

                  {isGroupOrder && isGroupAdmin ? (
                    <Square
                      borderBottomWidth="1px"
                      borderBottomStyle="solid"
                      borderBottomColor="lightLine"
                    >
                      <Section inset="md">
                        <GroupOrderLines
                          hostLine={{
                            dinerName: order.ordered_by.name,
                            isHost: true,
                            items: createItems(order.line_items),
                          }}
                          participantLines={participantLines}
                        />
                      </Section>
                    </Square>
                  ) : (
                    <Square
                      borderBottomWidth="1px"
                      borderBottomStyle="solid"
                      borderBottomColor="lightLine"
                      data-testid="menu-line-items-section"
                    >
                      <Section inset="md">
                        {(order.line_items || []).map((lineItem: ILineItem) => (
                          <Stack
                            key={lineItem.name}
                            y={order.line_items.length > 1 ? 'sm' : 'none'}
                          >
                            <Flex justifyContent="space-between">
                              <Flex>
                                <Stack as="span" x="xx-lg" width="16px">
                                  <Copy variant="body" color="primaryText">
                                    {lineItem.quantity}
                                  </Copy>
                                </Stack>
                                <Stack as="span" x="xx-lg">
                                  <Copy variant="body" color="primaryText">
                                    {lineItem.name}
                                  </Copy>
                                </Stack>
                              </Flex>
                              <Flex justifyContent="flex-end">
                                <Copy variant="lead" color="primaryText">
                                  {formatPenniesToCurrency(lineItem.price)}
                                </Copy>
                              </Flex>
                            </Flex>
                          </Stack>
                        ))}
                      </Section>
                    </Square>
                  )}

                  <Square
                    borderBottomWidth="1px"
                    borderBottomStyle="solid"
                    borderBottomColor="lightLine"
                    data-testid="payments-items-section"
                  >
                    <Section inset="md">
                      <Stack y="sm">
                        <ReceiptLineItem
                          title="Item subtotal"
                          amount={formatPenniesToCurrency(order.item_subtotal)}
                        />
                      </Stack>

                      {promoCodeAmount > 0 && (
                        <Stack y="sm">
                          <ReceiptLineItem
                            title="Promo code"
                            amount={`-${formatPenniesToCurrency(
                              promoCodeAmount
                            )}`}
                            color="success"
                          />
                        </Stack>
                      )}

                      <DeliveryFeeSection
                        order={order}
                        grubhubPlusPayment={grubhubPlusPayment}
                      />

                      <Stack y="sm">
                        <FeeLineItem
                          order={order}
                          feeAmount={calculateServiceFee(order)}
                          campaignFeeType={FeeType.SERVICE}
                          title="Service fee"
                        />
                      </Stack>

                      {getGenericFees(order).map(
                        (feeItem: IOrderChargesFeeItems) => (
                          <Stack y="sm" key={feeItem.name}>
                            <ReceiptLineItem
                              title={feeItem.name}
                              amount={formatPenniesToCurrency(
                                feeItem.calculated_amount
                              )}
                            />
                          </Stack>
                        )
                      )}

                      <Stack y="sm">
                        <ReceiptLineItem
                          title="Sales tax"
                          amount={formatPenniesToCurrency(order.sales_tax)}
                        />
                      </Stack>

                      <Stack y="sm">
                        <ReceiptLineItem
                          title="Tip"
                          amount={formatPenniesToCurrency(order.tip)}
                        />
                      </Stack>

                      <Stack y="sm">
                        <ReceiptLineItem
                          title="Order subtotal"
                          amount={formatPenniesToCurrency(order.total_charge)}
                        />
                      </Stack>

                      <Stack y="sm">
                        <ReceiptLineItem
                          title={capitalize(MEAL_CREDIT_ENTITY_NAME.singular)}
                          amount={`-${formatPenniesToCurrency(
                            order.loc_total
                          )}`}
                        />
                      </Stack>

                      {rewardsAmount > 0 && (
                        <Stack y="sm">
                          <ReceiptLineItem
                            title="Rewards"
                            amount={`-${formatPenniesToCurrency(
                              rewardsAmount
                            )}`}
                            color="success"
                          />
                        </Stack>
                      )}

                      {dinerPaymentAmount > 0 && (
                        <ReceiptLineItem
                          title={`${formatDinerName(
                            order.ordered_by.name
                          )} payment`}
                          amount={formatPenniesToCurrency(dinerPaymentAmount)}
                        />
                      )}
                    </Section>
                  </Square>
                </Square>
              </Stack>

              <Square
                dimension="2"
                bg="untintedBackground"
                className="print_cancelBorders"
              >
                <Section inset="md">
                  <Stack y="sm">
                    <Heading variant="h5">Your receipt</Heading>
                  </Stack>
                  <Stack y="sm">
                    <ReceiptLineItem
                      title={capitalize(MEAL_CREDIT_ENTITY_NAME.singular)}
                      amount={formatPenniesToCurrency(order.loc_total)}
                    />
                  </Stack>

                  <Stack y="sm">
                    <Flex justifyContent="space-between">
                      <div>
                        <Stack as="span" x="x-sm">
                          <Copy variant="lead" color="secondaryText">
                            Transaction fee
                          </Copy>
                        </Stack>
                        <Tooltip
                          maxWidth="290px"
                          content={
                            <p>
                              The 6% transaction fee covers the cost of your
                              Grubhub business account
                            </p>
                          }
                        >
                          <span>
                            <Copy variant="body" color="secondaryText">
                              <SvgInfo size="24px" />
                            </Copy>
                          </span>
                        </Tooltip>
                      </div>

                      <Flex justifyContent="flex-end">
                        {formatPenniesToCurrency(order.transaction_fee)}
                      </Flex>
                    </Flex>
                  </Stack>

                  <Flex justifyContent="space-between">
                    <Copy variant="lead" strong color="secondaryText">
                      Total
                    </Copy>
                    <Flex justifyContent="flex-end">
                      <Heading variant="h4">
                        {formatPenniesToCurrency(
                          order.loc_total + order.transaction_fee
                        )}
                      </Heading>
                    </Flex>
                  </Flex>
                </Section>
              </Square>
            </Column>
          </Row>
        </Section>
      </PrintContentContainer>
    </Container>
  ) : null;

  function formatAddress(orderDeliveryInfo: IDeliveryInfo): string {
    return `${orderDeliveryInfo.street_address1}, 
    ${orderDeliveryInfo.address_locality}, 
    ${orderDeliveryInfo.address_region}, 
    ${orderDeliveryInfo.postal_code}`;
  }

  function formatDinerName(dinerName: string): string {
    return `${dinerName.split(' ')[0]}'s`;
  }
}
